import axios from "axios";
import logger from "../utils/logger";
import { compressImage } from "../utils/imageCompression";
import { getUploadErrorMessage } from "../utils/uploadError";

const UPLOAD_PRESETS = {
  detection: {
    maxWidth: 1600,
    maxHeight: 1600,
    quality: 0.82,
    maxSizeMB: 1,
  },
  blister: {
    maxWidth: 1600,
    maxHeight: 1600,
    quality: 0.85,
    maxSizeMB: 1.5,
  },
};

const prepareImageForUpload = async (imageFile, preset = "detection") => {
  const options = UPLOAD_PRESETS[preset] || UPLOAD_PRESETS.detection;
  if (imageFile && typeof imageFile.size === "number") {
    logger.debug(
      `Original file size: ${(imageFile.size / 1024 / 1024).toFixed(2)} MB`,
    );
  }
  const compressed = await compressImage(imageFile, options);
  if (compressed && typeof compressed.size === "number") {
    logger.debug(
      `Compressed file size: ${(compressed.size / 1024 / 1024).toFixed(2)} MB`,
    );
  }
  return compressed;
};

const postImageUpload = async (endpoint, imageFile, preset, fields = {}) => {
  let fileToUpload = imageFile;
  try {
    fileToUpload = await prepareImageForUpload(imageFile, preset);
  } catch (error) {
    logger.warn("Image compression failed, using original file:", error);
    fileToUpload = imageFile;
  }

  const formData = new FormData();
  formData.append("file", fileToUpload);
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      formData.append(key, String(value));
    }
  });

  try {
    const response = await api.post(endpoint, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const data = response.data;
    return {
      ...data,
      capture_id: data.capture_id || null,
      timestamp: data.timestamp || null,
      gcs_uri: data.gcs_uri || null,
    };
  } catch (error) {
    throw new Error(getUploadErrorMessage(error));
  }
};

// Dynamic API URL detection for ngrok support
const getApiBaseUrl = () => {
  // Check if we're running on ngrok (external access)
  if (
    typeof window !== "undefined" &&
    window.location &&
    (window.location.hostname.includes("ngrok") ||
      window.location.hostname.includes("ngrok-free") ||
      window.location.hostname.includes("ngrok.io"))
  ) {
    const backendUrl = localStorage.getItem("backend_ngrok_url");
    if (backendUrl) {
      logger.debug("Using backend URL from localStorage:", backendUrl);
      return backendUrl;
    }

    const currentHost = window.location.hostname;
    logger.warn("Backend ngrok URL not found in localStorage.");
    logger.warn("Please set the backend URL manually in browser console:");
    logger.warn(
      'localStorage.setItem("backend_ngrok_url", "https://your-backend-url.ngrok-free.app");'
    );
    logger.warn("Then refresh the page.");

    return `https://${currentHost}`;
  }

  // Production build
  if (process.env.NODE_ENV === "production") {
    return (
      process.env.REACT_APP_BACKEND_API_URL_PRODUCTION ||
      process.env.REACT_APP_API_URL_PRODUCTION ||
      process.env.REACT_APP_API_URL ||
      "/api"
    );
  }

  // Local / Development
  return (
    process.env.REACT_APP_BACKEND_API_URL_LOCAL ||
    process.env.REACT_APP_API_URL_LOCAL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:8001"
  );
};

export { getApiBaseUrl };

const API_BASE_URL = getApiBaseUrl();

// Create axios instance with base configuration
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    if (typeof localStorage !== "undefined") {
      const token = localStorage.getItem("authToken");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Login's own 401 is handled by the login form (otherwise the reload hides its message)
    if (
      error.response?.status === 401 &&
      error.config?.url !== "/login" &&
      typeof window !== "undefined"
    ) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("userData");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const apiService = {
  // Authentication
  login: async (email, password) => {
    const response = await api.post("/login", {
      email,
      password,
    });
    return response.data;
  },

  // Barcode scanning
  scanBarcode: async (barcodeId) => {
    const formData = new FormData();
    formData.append("barcode_id", barcodeId);

    const response = await api.post("/scan", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  // Pill counting (legacy upload endpoint)
  countPills: (imageFile, confidence = 0.05, barcodeId = null) => {
    const fields = { confidence };
    if (barcodeId) fields.barcode_id = barcodeId;
    return postImageUpload("/upload", imageFile, "detection", fields);
  },

  // Intelligent pill counting with estimation
  countPillsWithEstimation: (
    imageFile,
    estimatedCount,
    confidence = 0.05,
    barcodeId = null
  ) => {
    const fields = { estimated_count: estimatedCount, confidence };
    if (barcodeId) fields.barcode_id = barcodeId;
    return postImageUpload("/count-pills", imageFile, "detection", fields);
  },

  // MMS Tablet Detection using backend YOLO model
  countMMSBlisterYolo: (
    imageFile,
    confidence = 0.25,
    barcodeId = null
  ) => {
    const fields = { confidence };
    if (barcodeId) fields.barcode_id = barcodeId;
    return postImageUpload("/count-mms-blister-yolo", imageFile, "blister", fields);
  },

  // Get available counting methods
  getCountMethods: async () => {
    const response = await api.get("/count-methods");
    return response.data;
  },

  // Submit count (legacy endpoint - requires mother_id and supplement_id)
  submitCount: async (data) => {
    const response = await api.post("/submit", data);
    return response.data;
  },

  // Submit counting to BigQuery tables (supports object payload or positional arguments, with nama_ibu and capture_id)
  submitCounting: async (
    barcodeIdOrPayload,
    jumlahPills,
    detectionType,
    arg4 = null,
    arg5 = null,
    arg6 = null
  ) => {
    let payload = {};

    if (
      typeof barcodeIdOrPayload === "object" &&
      barcodeIdOrPayload !== null &&
      !Array.isArray(barcodeIdOrPayload)
    ) {
      payload = {
        barcode_id: barcodeIdOrPayload.barcode_id,
        jumlah_pills: barcodeIdOrPayload.jumlah_pills,
        // != null so a correction to 0 tablets is still sent
        ...(barcodeIdOrPayload.jumlah_pills_koreksi != null && {
          jumlah_pills_koreksi: barcodeIdOrPayload.jumlah_pills_koreksi,
        }),
        detection_type: barcodeIdOrPayload.detection_type,
        ...(barcodeIdOrPayload.nama_ibu && {
          nama_ibu: barcodeIdOrPayload.nama_ibu,
        }),
        ...(barcodeIdOrPayload.capture_id && {
          capture_id: barcodeIdOrPayload.capture_id,
        }),
        ...(barcodeIdOrPayload.image_url && {
          image_url: barcodeIdOrPayload.image_url,
        }),
        ...(barcodeIdOrPayload.yolo_image && {
          yolo_image: barcodeIdOrPayload.yolo_image,
        }),
        ...(barcodeIdOrPayload.id_ibu && {
          id_ibu: barcodeIdOrPayload.id_ibu,
        }),
        ...(barcodeIdOrPayload.id_user_chp && {
          id_user_chp: barcodeIdOrPayload.id_user_chp,
        }),
        ...(barcodeIdOrPayload.chp_name && {
          chp_name: barcodeIdOrPayload.chp_name,
        }),
        ...(barcodeIdOrPayload.ai_metadata && {
          ai_metadata: barcodeIdOrPayload.ai_metadata,
        }),
      };
    } else {
      payload = {
        barcode_id: barcodeIdOrPayload,
        jumlah_pills: jumlahPills,
        detection_type: detectionType,
      };

      if (typeof arg4 === "string") {
        if (arg4.startsWith("cap_")) {
          payload.capture_id = arg4;
          if (arg5) payload.nama_ibu = arg5;
          if (arg6) payload.chp_name = arg6;
        } else {
          payload.nama_ibu = arg4;
          if (arg5) {
            if (typeof arg5 === "string" && arg5.startsWith("cap_")) {
              payload.capture_id = arg5;
            } else {
              payload.chp_name = arg5;
            }
          }
          if (arg6) {
            if (typeof arg6 === "string" && arg6.startsWith("cap_")) {
              payload.capture_id = arg6;
            } else {
              payload.chp_name = arg6;
            }
          }
        }
      } else if (arg4 !== null && arg4 !== undefined) {
        payload.nama_ibu = arg4;
      }
    }

    const response = await api.post("/submit-counting", payload);
    return response.data;
  },

  // Alias for submitCountingRecord
  submitCountingRecord: async (...args) => {
    return apiService.submitCounting(...args);
  },

  // Get history (legacy - from records table)
  getHistory: async (filters = {}) => {
    const response = await api.get("/records", { params: filters });
    return response.data;
  },

  // Get counting history from 3 counting tables (mms_pill_counting, calcium_tablet_counting, ttd_capsule_counting)
  getCountingHistory: async (limit = 1000) => {
    const response = await api.get("/counting-history", { params: { limit } });
    return response.data;
  },

  // Get statistics
  getStats: async () => {
    const response = await api.get("/records");
    return response.data;
  },

  // Export data
  exportData: async (filters = {}) => {
    const response = await api.get("/export/csv", {
      params: filters,
      responseType: "blob",
    });
    return response.data;
  },

  // Delete record (legacy - for records table)
  deleteRecord: async (recordId) => {
    const response = await api.delete(`/records/${recordId}`);
    return response.data;
  },

  // Update counting record in BigQuery
  updateCountingRecord: async (
    tableName,
    barcodeId,
    tanggalSubmit,
    jumlahPills
  ) => {
    const response = await api.put(
      `/counting-records/${encodeURIComponent(tableName)}/${encodeURIComponent(
        barcodeId
      )}?tanggal_submit=${encodeURIComponent(tanggalSubmit)}`,
      { jumlah_pills: jumlahPills }
    );
    return response.data;
  },

  // Delete counting record from BigQuery
  deleteCountingRecord: async (tableName, barcodeId, tanggalSubmit) => {
    const response = await api.delete(
      `/counting-records/${encodeURIComponent(tableName)}/${encodeURIComponent(
        barcodeId
      )}?tanggal_submit=${encodeURIComponent(tanggalSubmit)}`
    );
    return response.data;
  },

  // Settings - Confidence threshold
  setConfidenceThreshold: async (confidence) => {
    const formData = new FormData();
    formData.append("confidence", confidence.toString());

    const response = await api.post("/settings/confidence", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  getConfidenceThreshold: async () => {
    const response = await api.get("/settings/confidence");
    return response.data;
  },

  // Get direct/proxy URL for capture image
  getCaptureImageUrl: (captureId, detectionType = "mms_bottle", overlay = false) => {
    if (!captureId) return null;
    const baseUrl = getApiBaseUrl();
    return `${baseUrl}/captures/${encodeURIComponent(captureId)}/image?detection_type=${encodeURIComponent(detectionType)}${overlay ? "&overlay=true" : ""}`;
  },
};

export default apiService;
